/**
 * advisorChat.ts — client for the AI Strategy Advisor chat endpoint (T-501).
 *
 * Kept in its own module (not api.ts) so it is a self-contained unit — its own error type — and
 * so adding the advisor does not force every test that mocks `./api` to also stub the advisor
 * surface. It still shares the app's authenticated transport (`authedFetch`).
 *
 * TWO MODES, one request shape (T-524). Passing `onPartial` opts the call into SSE: the request
 * body is unchanged and only an `Accept: text/event-stream` header is added, so the server picks
 * the response writer. Without `onPartial` this is the original buffered call, untouched.
 *
 * Streaming degrades gracefully in three places, because a token stream has more ways to fail than
 * a single JSON body: a server or proxy that answers JSON anyway is read as buffered; a mid-stream
 * failure surfaces the partial text plus a clear error; and a stream that simply stops without a
 * terminal event is treated as INTERRUPTED rather than complete — a truncated answer that looks
 * finished is worse than a visible error.
 *
 * T-548 applies that same rule to the ordinary success path: a reply that stopped because it hit the
 * server's output ceiling arrives with `truncated: true` rather than looking like a finished answer.
 */
import { authedFetch } from './api'
import type { AdvisorContext, AdvisorMessage } from './advisorContext'

/** Cap on transcript turns sent per request — mirrors the server's Zod bound (≤20). */
export const MAX_TRANSCRIPT_TURNS = 20
/** Cap on a single message's characters — mirrors the server's Zod bound (≤4000). */
export const MAX_MESSAGE_CHARS = 4000

/**
 * A failed advisor turn, carrying the HTTP status (and, for a 429, the `scope`) so the panel can
 * distinguish a per-minute rate-limit (transient) from the per-user daily cap (`scope: 'daily'`)
 * from a transient upstream outage (503) from a config error (500) — §1.3 error states map off this.
 */
export class AdvisorError extends Error {
  status: number
  scope?: string
  constructor(status: number, message: string, scope?: string) {
    super(message)
    this.name = 'AdvisorError'
    this.status = status
    this.scope = scope
  }
}

/**
 * One completed advisor turn (T-548).
 *
 * `truncated` is part of the RESULT rather than a side-channel callback because it is a property of
 * the answer, not of its delivery: the reply stopped at the output ceiling instead of finishing.
 * Returning it makes it typed and impossible to forget — a caller that renders `text` has to decide
 * what to do about `truncated`, which is exactly the bug this fixes (the fact existed server-side
 * and simply never reached the user).
 */
export interface AdvisorReply {
  text: string
  /** The model ran out of room; `text` is the answer so far, cut mid-sentence or mid-word. */
  truncated: boolean
}

export interface AdvisorChatOptions {
  signal?: AbortSignal
  /**
   * Supply this to stream. Called with the text ACCUMULATED SO FAR after every token, so a React
   * caller can render it straight into state without keeping its own running buffer.
   *
   * It is deliberately not named `onDelta`: what arrives is the whole reply so far, not the
   * increment, and the previous name in this file's docs described a callback that did not exist.
   */
  onPartial?: (accumulated: string) => void
}

/** The message shown when a stream dies after the reply started arriving. */
const STREAM_INTERRUPTED = 'The advisor was interrupted before it finished. Please try again.'

/**
 * Read one SSE body, reporting partial text as it arrives, and resolve with the AUTHORITATIVE
 * final reply from the terminal `done` event.
 *
 * `done.reply` wins over the concatenated deltas by design: it is what lets the server correct a
 * reply after the fact (the T-523 numeric audit strips an invented number at stream end and sends
 * the corrected text), and it means a dropped delta cannot leave the client showing something the
 * server never approved. The server pins `concat(deltas) === done.reply` whenever nothing was
 * amended, so the two cannot drift silently.
 *
 * Frames are reassembled across chunk boundaries: the network splits wherever it likes, including
 * mid-JSON and mid-delimiter.
 */
async function readEventStream(
  body: ReadableStream<Uint8Array>,
  onPartial: (accumulated: string) => void,
): Promise<AdvisorReply> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let accumulated = ''
  let final: string | null = null
  let truncated = false

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let split: number
      while ((split = buffer.indexOf('\n\n')) !== -1) {
        const rawFrame = buffer.slice(0, split)
        buffer = buffer.slice(split + 2)

        const event = /^event: (.+)$/m.exec(rawFrame)?.[1]
        const rawData = /^data: (.+)$/m.exec(rawFrame)?.[1]
        if (!event || !rawData) continue // comment/keep-alive frame

        const data = JSON.parse(rawData) as {
          text?: string
          reply?: string
          error?: string
          truncated?: boolean
        }
        if (event === 'delta') {
          accumulated += data.text ?? ''
          onPartial(accumulated)
        } else if (event === 'done') {
          final = data.reply ?? accumulated
          truncated = data.truncated === true
        } else if (event === 'error') {
          // Status is 200 and cannot be revised — the headers went out when the stream opened.
          // `scope: 'stream'` is how the panel tells this apart from a pre-stream failure.
          throw new AdvisorError(200, data.error ?? STREAM_INTERRUPTED, 'stream')
        }
      }
    }
  } finally {
    reader.releaseLock()
  }

  if (final === null) throw new AdvisorError(200, STREAM_INTERRUPTED, 'stream')
  return { text: final, truncated }
}

/**
 * Send one advisor turn (the running transcript + the current CONTEXT) to
 * POST /api/advisor/chat and return the assistant's reply plus whether it was cut short (T-548).
 *
 * The transcript is trimmed to the last MAX_TRANSCRIPT_TURNS so a long conversation stays within
 * the server's bound (the server 400s an over-limit body — this pre-trims to avoid the round-trip).
 * Non-2xx throws an `AdvisorError` carrying the status (and `scope` when present). A model refusal
 * is returned by the server as a normal `{ reply }` (a safe redirect), not an error.
 */
export async function advisorChat(
  messages: AdvisorMessage[],
  context: AdvisorContext,
  options: AdvisorChatOptions = {},
): Promise<AdvisorReply> {
  const trimmed = messages.slice(-MAX_TRANSCRIPT_TURNS)
  const res = await authedFetch('/api/advisor/chat', {
    method: 'POST',
    headers: options.onPartial
      ? { 'Content-Type': 'application/json', Accept: 'text/event-stream' }
      : { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: trimmed, context }),
    signal: options.signal,
  })

  if (!res.ok) {
    let message = `Advisor request failed: ${res.status}`
    let scope: string | undefined
    try {
      const body = (await res.json()) as { error?: string; scope?: string }
      if (body?.error) message = body.error
      scope = body?.scope
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new AdvisorError(res.status, message, scope)
  }

  // Stream only if we asked for one AND the server actually sent one. If a proxy or an older
  // deployment answers JSON instead, fall through to the buffered read — the answer still arrives,
  // it just arrives all at once. That is the fallback the card requires, and it needs no flag.
  const isEventStream = res.headers.get('Content-Type')?.includes('text/event-stream') ?? false
  if (options.onPartial && isEventStream && res.body) {
    return readEventStream(res.body, options.onPartial)
  }

  const data = (await res.json()) as { reply?: string; truncated?: boolean }
  return { text: data.reply ?? '', truncated: data.truncated === true }
}
